import React from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ricerca, schedeCasino, sezioniAccese} from '../../moduli/leggi';
import {giochi as caricaGiochi} from '../../services/casinoApi';
import {inAnteprima} from '../../skin';
import {pagineAiutoAccese, pagineDelMenuAccese, vociDelConto, vociDellaRete} from './voci';
import IconaNova, {ICONA_AIUTO, ICONA_SEZIONE} from './IconaNova';

/* Le icone del menu sono disegnate (IconaNova), come quelle della testata:
   restano emoji solo quelle che la skin sceglie dal backoffice (le schede). */
const disegno = (nome) => <IconaNova nome={nome} misura={18}/>;

/**
 * IL MENU NOVA: un cassetto a sinistra sul telefono (si apre dall'hamburger o
 * da «Menu» nella barra in basso) e, dal computer in su, una colonna fissa
 * quando il modulo Testata ha Menu = «Colonna a sinistra». Con Menu = «In
 * testata» (di serie) dal computer questo menu non c'e': le sezioni, le
 * schede, il conto e l'Aiuto stanno in testata (TestataNova), e il cassetto
 * resta per telefono e tablet.
 *
 * Dentro, nell'ordine di Nova: il gruppo Casinò con le sezioni accese della
 * skin e le categorie dei giochi; Account (solo per chi e' dentro); Aiuto.
 *
 * CERCA GIOCHI NON E' QUI dal computer in su: la casella sta in testata, in
 * alto a destra (modulo Ricerca giochi). Sul telefono in testata non c'e'
 * posto, e la voce resta in cima al menu: apre la pagina dei risultati, che
 * ha la sua casella.
 *
 * ACCOUNT SOLO PER CHI E' DENTRO, E SOLO DAL COMPUTER: da ospiti Accedi e
 * Registrati sono in testata (o nella barra in basso sul telefono). Sul
 * telefono il conto, con Esci, sta nel pannello «Conto» della barra, e qui
 * resta solo Gioco responsabile, sotto Aiuto.
 *
 * AIUTO, prima e dopo l'accesso: Chi siamo, Regolamento, Assistenza, coi
 * titoli e le pagine accese del modulo Aiuto.
 *
 * LA HOME NON E' UNA VOCE: ci porta il logo in testata (e «Home» nella barra
 * in basso sul telefono). Una voce in piu' che fa la stessa cosa allunga il
 * menu e basta.
 *
 * «IL MIO ACCOUNT» e' una tendina: Profilo, Movimenti, Lista scommesse,
 * Cambio password, Cassa, ognuna solo a chi ha la sua voce nel gettone
 * (utils/permessi.js, come il menu delle altre skin); senza nessuna voce
 * la tendina non c'e'. Si apre da sola quando si e' in una di quelle
 * pagine. Gioco responsabile resta fuori, a vista: deve essere raggiungibile
 * con un tocco. Esci, dal computer in su, e' in testata in alto a destra.
 *
 * «LA MIA RETE» e' la seconda tendina, per chi ha qualcuno sotto: Lista
 * utenti, Giroconto, Report, ognuna a chi ha la sua voce (voci.js). Un
 * giocatore non ne ha nessuna e la tendina non c'e'.
 *
 * LE SCHEDE DEL CASINO': quando la skin ne ha (Moduli › Casinò o Live casino
 * › Schede, dal backoffice), Casinò e Live casino diventano tendine, prima
 * Casinò e poi Live casino: dentro «Tutti i giochi» e una voce per scheda,
 * che apre la pagina della scheda: la lobby con TUTTI i suoi giochi, a
 * pagine (/casino?scheda=top10). Le altre sezioni restano voci semplici, sotto.
 *
 * Senza schede, come prima: le sezioni accese e sotto le CATEGORIE che manda
 * il fornitore (le stesse della lobby): il menu non ne scrive nessuna. Senza
 * fornitore al loro posto ci sono righe vuote tratteggiate (lo scheletro,
 * come nella lobby) e una frase che dice perche'.
 *
 * PROMOZIONI, SPAZIO VIP E TORNEI (modulo Pagine) stanno in un gruppo loro, sopra
 * Aiuto, e SOLO quando la pagina e' accesa: un tasto che non porta da
 * nessuna parte e' peggio di nessun tasto. La voce FAQ di Nova non c'e'.
 */
export default function MenuNova({aperto, onChiudi, dentro}) {
  const {t} = useTranslation();
  const posizione = useLocation();
  /* La categoria scelta sta nell'indirizzo (/casino?categoria=Slot): il
     NavLink guarda solo il percorso, e senza questo si accenderebbero tutte. */
  const categoriaScelta = posizione.pathname === '/casino' ? new URLSearchParams(posizione.search).get('categoria') : null;
  const schedaScelta = new URLSearchParams(posizione.search).get('scheda');
  const [categorie, setCategorie] = React.useState(null);   // null: sto caricando

  const sezioni = sezioniAccese();
  const conCasino = sezioni.some((s) => s.chiave === 'casino');
  const schede = {casino: schedeCasino('casino'), liveCasino: schedeCasino('liveCasino')};
  const conSchede = sezioni.some((s) => (schede[s.chiave] || []).length > 0);
  /* Con le schede: prima Casinò, poi Live casino, poi le altre nell'ordine del catalogo. */
  const inOrdine = conSchede
    ? ['casino', 'liveCasino'].map((k) => sezioni.find((s) => s.chiave === k)).filter(Boolean)
        .concat(sezioni.filter((s) => s.chiave !== 'casino' && s.chiave !== 'liveCasino'))
    : sezioni;

  React.useEffect(() => {
    /* Le categorie del fornitore servono solo quando la skin non ha schede. */
    if (conSchede) return undefined;
    let vivo = true;
    caricaGiochi('casino')
        .then((elenco) => {
          const viste = [];
          elenco.forEach((g) => g.categorie.forEach((c) => { if (!viste.includes(c)) viste.push(c); }));
          if (vivo) setCategorie(viste);
        })
        .catch(() => vivo && setCategorie([]));
    return () => { vivo = false; };
  }, [conSchede]);

  /* Le pagine di Aiuto accese e le voci del conto: le stesse della testata (voci.js). */
  const aiuto = pagineAiutoAccese(t);
  /* Le pagine del menu accese (Promozioni, Tornei, VIP…): le stesse della testata (voci.js). */
  const pagine = pagineDelMenuAccese(t);
  const segnaposto = ricerca().segnaposto;
  const cercaScritta = !segnaposto || segnaposto === 'Cerca giochi' ? t('Cerca giochi') : segnaposto;
  const nelConto = /^\/my-account\/(profilo|lista-movimenti|lista-scommesse|sicurezza|cassa)/.test(posizione.pathname);
  const nellaRete = /^\/my-account\/(lista-utenti|giroconto|report)/.test(posizione.pathname);
  /* LE VOCI DEL CONTO SONO QUELLE DEL GETTONE (utils/permessi.js), come nelle
     altre skin: prima questa tendina le scriveva tutte e cinque a chiunque
     fosse dentro, e chi non aveva una voce trovava «non disponibile» dietro. */
  const vociConto = vociDelConto(t);
  const vociRete = vociDellaRete(t);
  /* Un tocco su un collegamento chiude il cassetto: si e' arrivati. */
  const alTocco = (e) => { if (e.target.closest('a')) onChiudi(); };
  const voce = (a, k) => (
    <NavLink key={k || a.to} to={a.to} end={!!a.esatto}
      className={({isActive}) => 'nova-voce' + (a.classe ? ' ' + a.classe : '') + ((a.attiva === undefined ? isActive : a.attiva) ? ' active' : '')}>
      <span>{a.icona}</span>{a.testo}
    </NavLink>
  );

  return (
    <>
      <div className={'nova-velo' + (aperto ? ' aperto' : '')} onClick={onChiudi} aria-hidden={'true'}/>
      <aside className={'nova-menu' + (aperto ? ' aperto' : '')} aria-label={t('Menu')} onClick={alTocco}>
        {ricerca().casella && (conCasino || sezioni.some((x) => x.chiave === 'liveCasino')) && (
          <NavLink to={'/cerca'} className={'nova-cerca nova-solo-telefono'}><span>{disegno('lente')}</span> {cercaScritta}</NavLink>
        )}

        <p className={'nova-etichetta'}>{t('Casinò')}</p>
        {inOrdine.map((s) => {
          const sue = schede[s.chiave] || [];
          if (!sue.length) {
            return voce({
              to: s.percorso, icona: disegno(ICONA_SEZIONE[s.chiave] || 'giochi'), testo: t(s.nome),
              attiva: s.chiave === 'casino' ? (posizione.pathname === '/casino' && !categoriaScelta && !schedaScelta) : undefined,
            });
          }
          const qui = posizione.pathname === s.percorso;
          return (
            <Tendina key={s.chiave} icona={disegno(ICONA_SEZIONE[s.chiave] || 'giochi')} testo={t(s.nome)} aperta={qui}>
              {voce({to: s.percorso, icona: '▦', testo: t('Tutti i giochi'), attiva: qui && !schedaScelta && !categoriaScelta}, 'tutti-' + s.chiave)}
              {sue.map((x) => voce({
                to: s.percorso + '?scheda=' + encodeURIComponent(x.id), icona: x.icona || '▸', testo: x.nome,
                attiva: qui && schedaScelta === x.id,
              }, 'scheda-' + s.chiave + '-' + x.id))}
            </Tendina>
          );
        })}
        {conCasino && !conSchede && categorie !== null && categorie.map((c) => voce({
          to: '/casino?categoria=' + encodeURIComponent(c), icona: '▸', testo: c, attiva: categoriaScelta === c,
        }, 'cat-' + c))}
        {/* Senza categorie il posto vuoto lo vede solo chi compone la skin: a chi gioca il menu finisce li'. */}
        {conCasino && !conSchede && categorie !== null && categorie.length === 0 && inAnteprima() && (
          <div className={'nova-categorie-vuote'}>
            <div aria-hidden={'true'}>
              {[0, 1, 2, 3].map((i) => <span key={i} className={'nova-voce vuota'}><span>▸</span><i/></span>)}
            </div>
            <p>{t('Le categorie arrivano dal fornitore dei giochi: senza fornitore non ce ne sono.')}</p>
          </div>
        )}

        {pagine.length > 0 && (
          <>
            <p className={'nova-etichetta'}>{t('Promozioni')}</p>
            {pagine.map((p) => voce({to: p.percorso, icona: p.icona || '•', testo: p.titolo}))}
          </>
        )}

        {/* Sul telefono il conto sta nel pannello «Conto» della barra: qui solo dal computer. */}
        {dentro && (
          <div className={'nova-solo-computer'}>
            <p className={'nova-etichetta'}>{t('Account')}</p>
            {vociConto.length > 0 && (
              <Tendina icona={disegno('utente')} testo={t('Il mio account')} aperta={nelConto}>
                {vociConto.map((a) => voce({...a, icona: disegno(a.icona)}))}
              </Tendina>
            )}
            {vociRete.length > 0 && (
              <Tendina icona={disegno('rete')} testo={t('La mia rete')} aperta={nellaRete}>
                {vociRete.map((a) => voce({...a, icona: disegno(a.icona)}))}
              </Tendina>
            )}
            {voce({to: '/gioco-responsabile', icona: disegno('scudo'), testo: t('Gioco responsabile')})}
          </div>
        )}

        {(aiuto.length > 0 || dentro) && (
          <p className={'nova-etichetta' + (aiuto.length ? '' : ' nova-solo-telefono')}>{t('Aiuto')}</p>
        )}
        {dentro && voce({to: '/gioco-responsabile', icona: disegno('scudo'), testo: t('Gioco responsabile'), classe: 'nova-solo-telefono'}, 'gr-telefono')}
        {aiuto.map((p) => voce({to: p.percorso, icona: disegno(ICONA_AIUTO[p.chiave] || 'documento'), testo: p.titolo}))}
      </aside>
    </>
  );
}

/**
 * Una voce che apre e chiude le sue sottovoci. `aperta` la apre da sola (si
 * e' in una delle sue pagine); poi decide chi la tocca.
 */
function Tendina({icona, testo, aperta, children}) {
  const [su, setSu] = React.useState(!!aperta);
  React.useEffect(() => { if (aperta) setSu(true); }, [aperta]);
  return (
    <div className={'nova-tendina' + (su ? ' aperta' : '')}>
      <button type={'button'} className={'nova-voce'} aria-expanded={su} onClick={() => setSu((v) => !v)}>
        <span>{icona}</span>{testo}<span className={'nova-freccia'} aria-hidden={'true'}>▾</span>
      </button>
      {su && <div className={'nova-sottovoci'}>{children}</div>}
    </div>
  );
}
